import { studioDayKey, studioDayStartIso } from '@atink/domain';

/**
 * The artist-facing "this week" (artists-6 / 7w8g): the CALENDAR week, Monday to
 * Sunday, in studio (Melbourne) days. One window for the dashboard header tile,
 * the Week tab and the Earnings results, so the three never disagree. (The
 * manager's Targets board keeps its own Thu–Tue operational week.)
 */

const DAY_MS = 86_400_000;

function mondayMs(iso: string): number {
  const [y, m, d] = studioDayKey(iso).split('-').map(Number) as [number, number, number];
  const dayMs = Date.UTC(y, m - 1, d);
  const sinceMonday = (new Date(dayMs).getUTCDay() + 6) % 7;
  return dayMs - sinceMonday * DAY_MS;
}

const keyOf = (ms: number) => new Date(ms).toISOString().slice(0, 10);

/** Monday..Sunday studio-day keys (YYYY-MM-DD) of the calendar week holding `iso`. */
export function calendarWeekKeys(iso: string): [string, string] {
  const monday = mondayMs(iso);
  return [keyOf(monday), keyOf(monday + 6 * DAY_MS)];
}

/** The seven studio-day keys, Monday first, of the calendar week holding `iso`. */
export function calendarWeekDays(iso: string): string[] {
  const monday = mondayMs(iso);
  return Array.from({ length: 7 }, (_, i) => keyOf(monday + i * DAY_MS));
}

/** The studio day after `key` (pure calendar arithmetic, no host zone). */
export function nextStudioDayKey(key: string): string {
  const [y, m, d] = key.split('-').map(Number) as [number, number, number];
  return keyOf(Date.UTC(y, m - 1, d + 1));
}

/**
 * Midnight (Melbourne) at the start of the studio day AFTER the one holding
 * `iso`. A top-up instalment due on a day is only missed once that day is over
 * (packages/db sweeps: nowDay > dueDay), so its "missed" age counts from here,
 * not from the due day's own midnight (bgg4 #9).
 */
export function startOfNextStudioDayIso(iso: string): string {
  return studioDayStartIso(nextStudioDayKey(studioDayKey(iso)));
}
